import { ArrowContainer, Popover } from 'react-tiny-popover';
import React, { useState } from 'react';
import { HiDotsVertical } from 'react-icons/hi';
import styled from 'styled-components';
import { StaticImage } from 'gatsby-plugin-image';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import { CachePolicies, useFetch } from 'use-http';
import { HSpace, VSpace } from '@/components/spacing';
import { Container } from '@/features/details/sessions-type-chip';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';
import { ExpVersion } from '@/webapi/use-analytics-api';
import { countDays } from '@/features/details/date-pick/quick-range-select';
import { routes } from '@/webapi/routes';
import { Spinner } from '@/Spinner';
import { longPoll } from '@/utils/sync';

function VariationsList({
  onClick,
  versionSelectOpen,
  versions,
  selectedVersion,
  currentVersion,
  selectVersion,
}: {
  onClick: (e) => void;
  versionSelectOpen: boolean;
  versions?: Array<ExpVersion>;
  selectedVersion?: number;
  currentVersion?: number;
  selectVersion?: (value: ((prevState: number) => number) | number) => void;
}) {
  return (
    <VersionSelector>
      <Row>
        <Column>
          {versions
            ?.filter(
              (v) =>
                isAfterMinDate(new Date(v.startDate)) &&
                (versionSelectOpen || selectedVersion === v.version),
            )
            ?.map((v) => (
              <Version
                isSelected={selectedVersion === v.version}
                key={v.version}
                onClick={(e) => {
                  e.stopPropagation();
                  selectVersion(v.version);
                }}
              >
                Version {v.version} -{` `}
                {v?.version === currentVersion ? `current ` : ``} (
                {countDays(v)} Days)
              </Version>
            ))}
        </Column>
        <Toggle onClick={onClick}>
          {versionSelectOpen ? (
            <IoIosArrowUp size={15} />
          ) : (
            <IoIosArrowDown size={15} />
          )}
        </Toggle>
      </Row>
    </VersionSelector>
  );
}

export function DownloadOrdersChip({
  metricsStart,
  versions,
  expId,
}: {
  metricsStart?: string;
  expId?: string;
  versions?: Array<ExpVersion>;
}) {
  const { post } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
    },
  }));
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [versionSelectOpen, setVersionSelectOpen] = useState(false);
  const [versionSelec2tOpen, setVersion2SelecteOpen] = useState(false);
  const currentVersion = getCurrentVersion(versions);
  const [selectedVersion, selectVersion] = useState(currentVersion);

  const notAvailable = isNotOrderDownloadAvailable(metricsStart);

  const onMetricsExport = async (e) => {
    e.stopPropagation();
    if (loading) {
      return;
    }
    setLoading(true);
    const downloadLink = routes.exportStats(expId);
    await downloadFile(downloadLink, `stats`, expId, setLoading);
  };
  const onOrdersExport = async (e) => {
    e.stopPropagation();
    if (loading) {
      return;
    }
    setLoading(true);
    const action = async () => {
      const result = await post(routes.exportOrders, {
        longPol: true,
        experienceId: expId,
        version: selectedVersion,
      });
      return result.downloadLink;
    };
    const downloadLink = await longPoll<string>(action, 1000);
    await downloadFile(downloadLink, `orders`, expId, setLoading);
  };

  const onBestSellertsExport = async (e) => {
    e.stopPropagation();
    if (loading) {
      return;
    }
    setLoading(true);
    const action = async () =>
      (
        await post(routes.exportOrders, {
          experienceId: expId,
          longPol: true,
          byLineItems: true,
          version: selectedVersion,
        })
      ).downloadLink;
    const downloadLink = await longPoll<string>(action, 1000);
    await downloadFile(downloadLink, `best_sellers`, expId, setLoading);
  };

  const bsExportEnabled = useFeatureBit(FeatureBit.BEST_SELL_EXPORT);
  if (notAvailable) {
    return null;
  }

  return (
    <Popover
      onClickOutside={() => setOpen(!open)}
      isOpen={open}
      positions={[`bottom`, `left`, `right`]}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor="white"
          arrowSize={20}
        >
          <PopoverContainer loading={loading}>
            <SubTitle>Export experience data</SubTitle>
            <VSpace />
            <TwoColumns>
              <Description>
                Download experience
                <br /> data to a CSV file
              </Description>
              <DownloadBtn disabled={loading} onClick={onMetricsExport}>
                {loading ? (
                  <Spinner color="white" mt="0px" size={20} />
                ) : (
                  <DownIcon />
                )}
              </DownloadBtn>
            </TwoColumns>
            <VSpace />
            <Separator />
            <VSpace />
            <SubTitle>Export order numbers</SubTitle>
            <VSpace />
            <TwoColumns>
              <Description>
                Download order numbers <br />
                for each variation
                <br /> to a CSV file
              </Description>
              <DownloadBtn onClick={onOrdersExport}>
                {loading ? (
                  <Spinner color="white" mt="0px" size={20} />
                ) : (
                  <DownIcon />
                )}
              </DownloadBtn>
            </TwoColumns>
            <VSpace />
            <VariationsList
              versions={versions}
              selectedVersion={selectedVersion}
              currentVersion={currentVersion}
              selectVersion={selectVersion}
              onClick={(e) => {
                e.stopPropagation();
                setVersionSelectOpen(!versionSelectOpen);
              }}
              versionSelectOpen={versionSelectOpen}
            />
            <Separator />
            <VSpace />
            {bsExportEnabled && (
              <>
                <SubTitle>Export Best-Selling items</SubTitle>
                <VSpace />
                <TwoColumns>
                  <Description>
                    Download the list of best selling items <br />
                    for each variation
                    <br /> to a CSV file
                  </Description>
                  <DownloadBtn onClick={onBestSellertsExport}>
                    {loading ? (
                      <Spinner color="white" mt="0px" size={20} />
                    ) : (
                      <DownIcon />
                    )}
                  </DownloadBtn>
                </TwoColumns>
                <VSpace />
                <VariationsList
                  versions={versions}
                  selectedVersion={selectedVersion}
                  currentVersion={currentVersion}
                  selectVersion={selectVersion}
                  onClick={(e) => {
                    e.stopPropagation();
                    setVersion2SelecteOpen(!versionSelec2tOpen);
                  }}
                  versionSelectOpen={versionSelec2tOpen}
                />
              </>
            )}
          </PopoverContainer>
        </ArrowContainer>
      )}
    >
      <Container>
        <HSpace />
        <ChipWrap onClick={() => setOpen(!open)}>
          <HiDotsVertical />
        </ChipWrap>
      </Container>
    </Popover>
  );
}
const Toggle = styled.div`
  margin-top: 4px;
  align-self: start;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    opacity: 0.5;
    cursor: pointer;
  }
`;
const Row = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr;
  grid-gap: 1rem;
`;
const Column = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 310px;
  width: 160px;
  overflow-y: scroll;
`;
const Version = styled.div<{ isSelected: boolean }>`
  padding: 0.75rem;
  ${(p) => (p.isSelected ? `color: blue;` : ``)}
  &:hover {
    opacity: 0.5;
    cursor: pointer;
  }
`;
const VersionSelector = styled.div`
  padding: 1.5rem;
  background-color: #f7f7f7;
  color: #505050;

  font-family: Inter, serif;
  border-radius: 8px;
`;
const Separator = styled.span`
  background: #ebebeb;
  height: 1px;
  margin: 0.5rem 0;
  width: 100%;
`;

const PopoverContainer = styled.div`
  user-select: none;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  background-color: white;
  padding: 2rem 3rem;
  border: solid 1px rgba(151, 151, 151, 0.1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.09), 0 1px 4px 0 rgba(7, 6, 6, 0.04);
  * {
    opacity: ${(p) => (p.loading ? 0.8 : 1)};
  }
`;
export const TwoColumns = styled.div`
  font-family: Inter, serif;
  display: grid;
  grid-template-columns: 3fr 1fr;
  grid-gap: 3rem;
`;
const SubTitle = styled.span`
  font-family: Inter, serif;
  font-size: 1.3rem;
  font-weight: 600;
  letter-spacing: -0.25px;
  color: #505050;
`;

const Description = styled.span`
  font-size: 1.1rem;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: -0.25px;
  color: #505050;
`;
const DownloadBtn = styled.button`
  justify-self: center;
  align-self: center;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #3b47ff;
  height: 25px;
  width: 35px;
  border-radius: 20px;
  &:hover {
    cursor: ${(p) => (p.disabled ? `default` : `pointer`)};
    opacity: ${(p) => (p.disabled ? 0.5 : 1)};
  }
  &:active {
    cursor: default;
    opacity: 1;
  }
`;
const ChipWrap = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;

  cursor: pointer;
  background: transparent;

  font-size: 1.2rem;
  font-weight: 500;
  font-family: 'JetBrains Mono', serif;
  user-select: none;
  :hover {
    opacity: 0.5;
    box-shadow: none;
  }
  :active {
    opacity: 1;
  }

  color: #545454;

  border: 1px solid rgba(0, 0, 0, 0.2);

  box-shadow: 0 1px 4px 0 rgba(68, 72, 80, 0.08);

  border-radius: 2rem;
  padding: 0.85rem 1.5rem;
`;

function isNotOrderDownloadAvailable(metricsStart: string) {
  if (isLocalDev()) {
    return false;
  }
  return !(
    useFeatureBit(FeatureBit.DOWNLOAD_EXP_ORDERS) &&
    isAfterMinDate(new Date(metricsStart))
  );
}

function DownIcon() {
  return (
    <StaticImage
      height={15}
      loading="eager"
      placeholder="none"
      src="../../assets/download.png"
      alt="download"
    />
  );
}

async function downloadFile(
  downloadUrl: string,
  fileName: string,
  expId: string,
  setLoading: (value: ((prevState: boolean) => boolean) | boolean) => void,
) {
  if (downloadUrl) {
    try {
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement(`a`);
      a.href = url;
      a.setAttribute(`download`, `${fileName}_${expId}.csv`);
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.log(`failed downloading csv report`, e);
    } finally {
      setLoading(false);
    }
  }
}

function isAfterMinDate(date: Date) {
  if (isLocalDev()) {
    return true;
  }
  return date.valueOf() >= new Date(`2024-06-23`).valueOf();
}

function isLocalDev() {
  return (
    window?.location?.hostname ===
      `pre-prod--kind-noether-230989.netlify.app` ||
    window?.location?.hostname === `vsly.local`
  );
}
function getCurrentVersion(versions: Array<ExpVersion>) {
  return versions?.reduce((p, c) => (p < c?.version ? c?.version : p), 1);
}
